import PageAnimator from '../../components/ui/PageAnimator';
import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import TransactionsTable from '../../features/transactions/components/TransactionsTable';
import DateRangeFilter from '../../components/ui/DateRangeFilter';
import { getTransactions, getTransactionsKPIs } from '../../features/transactions/api/transactionsAPI';
import { TRANSACTION_STATUSES_LABELS } from '../../constants/transactionStatuses';
import { TRANSACTION_TYPES_LABELS } from '../../constants/transactionTypes';
import { Banknote, TrendingUp, AlertTriangle, Briefcase, Archive } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import SearchBar from '../../features/transactions/components/SearchBar';
import CustomSelect from '../../components/ui/CustomSelect';

export default function Transactions() {
 const [searchParams, setSearchParams] = useSearchParams();
 const [transactions, setTransactions] = useState([]);
 const [loading, setLoading] = useState(true);
 const [kpis, setKpis] = useState(null);
 const [dateRange, setDateRange] = useState({});
 const [showArchived, setShowArchived] = useState(() => searchParams.get('archived') === 'true');
 
 const [filters, setFilters] = useState({
 status: searchParams.get('status') || '',
 type: searchParams.get('type') || '',
 search: searchParams.get('search') || '',
 archived: searchParams.get('archived') === 'true'
 });

 const fetchTransactions = async () => {
 setLoading(true);
 try {
 const data = await getTransactions({ ...filters, archived: showArchived, ...dateRange });
 setTransactions(data);
 } catch (error) {
 console.error("Erreur de chargement", error);
 } finally {
 setLoading(false);
 }
 };

 const fetchKpis = async (start = null, end = null) => {
 try {
 const data = await getTransactionsKPIs(start, end);
 setKpis(data);
 } catch (error) {
 console.error("Erreur KPI", error);
 }
 };

  useEffect(() => {
    fetchTransactions();
    fetchKpis(dateRange.start, dateRange.end);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, showArchived, dateRange]);

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.status) params.set('status', filters.status);
    if (filters.type) params.set('type', filters.type);
    if (filters.search) params.set('search', filters.search);
    if (showArchived) params.set('archived', 'true');
    
    if (params.toString() !== searchParams.toString()) {
      setSearchParams(params, { replace: true });
    }
  }, [filters, showArchived, searchParams, setSearchParams]);

 const handleFilterChange = (e) => {
 const { name, value } = e.target;
 setFilters(prev => ({ ...prev, [name]: value }));
 };

 const handleDateFilter = (start, end) => {
 setDateRange({ start, end });
 };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    show: { opacity: 1, y: 0, transition: { duration: 0.4, ease: "easeOut" } }
  };

  return (
    <div className="p-8 bg-workspace min-h-screen">
      <PageAnimator>
            {/* Header Premium */}
            <div
                className="
                  relative z-20
                  flex shrink-0 flex-col
                  gap-6
                  overflow-visible
                  workspace-card
                  bg-cover bg-center bg-no-repeat
                  p-4 sm:p-6
                  mb-6
                  xl:flex-row xl:items-center xl:justify-between
                "
                style={{ backgroundImage: "url('/assets/backgrounds/castle_bg.png')" }}
              >
                <div
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-0 z-0 rounded-[var(--hp-radius-lg)] bg-overlay"
                />

                <div className="relative z-10 flex min-w-0 shrink-0 flex-col items-start gap-3 sm:flex-row sm:items-center sm:gap-4">
                  <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-secondary text-inverse shadow-hp-sm">
                    <Briefcase className="h-6 w-6" />
                  </div>

                  <div className="min-w-0">
                    <h1 className="text-2xl font-bold text-inverse">
                      {showArchived ? "Transactions archivées" : "Transactions en cours"}
                    </h1>
                    <p className="mt-1 text-sm text-inverse opacity-80">
                      {showArchived ? "Consultez l'historique de vos opérations clôturées." : "Gérez le cycle de vie de vos opérations d'investissement."}
                    </p>
                  </div>
                </div>

                <div className="relative z-50 flex flex-col md:flex-row items-stretch md:items-center gap-3">
                    {/* Bouton Voir archivées / Voir actives */}
                    <button
                      type="button"
                      onClick={() => setShowArchived(prev => !prev)}
                      className={`inline-flex items-center justify-center gap-2 px-3.5 py-2 rounded-lg text-xs font-semibold transition-all duration-200 h-[38px] shadow-hp-sm border ${
                        showArchived
                          ? 'bg-[var(--hp-warning-500)] text-inverse border-[var(--hp-warning-600)] hover:bg-[var(--hp-warning-600)]'
                          : 'bg-surface text-primary border-[var(--hp-color-border-muted)] hover:bg-surface-muted'
                      }`}
                      title={showArchived ? "Afficher les opérations en cours" : "Afficher les transactions archivées ou clôturées"}
                    >
                      <Archive className="h-4 w-4" />
                      <span>{showArchived ? "Voir actives" : "Voir archivées"}</span>
                    </button>

                    <div className="w-48">
                      <CustomSelect 
                        value={filters.type || ''} 
                        onChange={(val) => handleFilterChange({ target: { name: 'type', value: val } })}
                        options={[
                          { value: "", label: "Tous les types" },
                          ...Object.entries(TRANSACTION_TYPES_LABELS).map(([key, label]) => ({
                            value: key,
                            label: label
                          }))
                        ]}
                      />
                    </div>

                    <div className="w-48">
                      <CustomSelect 
                        value={filters.status || ''} 
                        onChange={(val) => handleFilterChange({ target: { name: 'status', value: val } })}
                        options={[
                          { value: "", label: "Tous les statuts" },
                          ...Object.entries(TRANSACTION_STATUSES_LABELS).map(([key, label]) => ({
                            value: key,
                            label: label
                          }))
                        ]}
                      />
                    </div>

                    <div className="text-primary bg-surface rounded-md h-[38px] flex items-center">
                      <DateRangeFilter onFilterChange={handleDateFilter} />
                    </div>
                </div>
            </div>

            {kpis && (
            <motion.div 
                variants={containerVariants}
                initial="hidden"
                animate="show"
                className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8"
            >
                <motion.div variants={itemVariants} className="workspace-card-elevated flex items-center border-l-4 border-l-secondary">
                    <div className="p-3 rounded-xl bg-secondary-50 text-secondary mr-4">
                        <Briefcase className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-subtle font-medium">Montant Cible / Engagé</p>
                        <p className="text-xl font-bold text-primary">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.totalEngage)}</p>
                    </div>
                </motion.div>
                
                <motion.div variants={itemVariants} className="workspace-card-elevated flex items-center border-l-4 border-l-[var(--hp-success-500)]">
                    <div className="p-3 rounded-xl bg-[var(--hp-success-50)] text-[var(--hp-success-600)] mr-4">
                        <Banknote className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-subtle font-medium">Montant Collecté</p>
                        <p className="text-xl font-bold text-primary">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.totalCollecte)}</p>
                    </div>
                </motion.div>

                <motion.div variants={itemVariants} className="workspace-card-elevated flex items-center border-l-4 border-l-[var(--hp-info-500)]">
                    <div className="p-3 rounded-xl bg-[var(--hp-info-50)] text-[var(--hp-info-600)] mr-4">
                        <TrendingUp className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-subtle font-medium">Intérêts / Rendements</p>
                        <p className="text-xl font-bold text-primary">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.interetsAttendus)}</p>
                    </div>
                </motion.div>

                <motion.div variants={itemVariants} className="workspace-card-elevated flex items-center border-l-4 border-l-[var(--hp-danger-500)]">
                    <div className="p-3 rounded-xl bg-[var(--hp-danger-50)] text-[var(--hp-danger-600)] mr-4">
                        <AlertTriangle className="w-6 h-6" />
                    </div>
                    <div>
                        <p className="text-sm text-[var(--hp-danger-700)] font-medium">Retards de flux ({kpis.retardsCount})</p>
                        <p className="text-xl font-bold text-[var(--hp-danger-900)]">{new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(kpis.retardsMontant)}</p>
                    </div>
                </motion.div>
            </motion.div>
            )}

            <TransactionsTable
              transactions={transactions}
              loading={loading}
              onRefresh={() => fetchTransactions()}
              isArchivedView={showArchived}
              paginationResetKey={JSON.stringify([filters.status, filters.type, filters.search, dateRange.start, dateRange.end])}
            />
      </PageAnimator>

      <AnimatePresence>
        <motion.div 
            initial={{ opacity: 0, y: 50, scale: 0.95 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 50, scale: 0.95 }}
            transition={{ duration: 0.4, type: "spring", stiffness: 300, damping: 30 }}
            className="fixed bottom-6 left-1/2 -translate-x-1/2 z-[60] flex items-center justify-center pointer-events-none"
        >
            <div className="pointer-events-auto bg-[rgb(var(--hp-surface-rgb)/85%)] backdrop-blur-xl p-3 rounded-[2rem] shadow-hp-xl border border-workspace flex items-center gap-3 ring-1 ring-black/5 hover:shadow-hp-2xl transition-all duration-300">
                <SearchBar
                    search={filters.search || ''}
                    onSearch={(val) => handleFilterChange({ target: { name: 'search', value: val }})}
                />
            </div>
        </motion.div>
      </AnimatePresence>
    </div>
  );
}
